import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Box } from "../../../../../../shared/Box/Box";
import { Table as SharedTable } from "../../../../../../shared/Table/Table";
import { useAppTranslation } from "../../../../../../i18n/useAppTranslation";
import { Skeleton } from "../../../../../../shared/Skeleton/Skeleton";
import { Empty } from "../../../../../../shared/Empty/Empty";
import { SortAdornment } from "../../../../../../shared/SortAdornment/SortAdornment";
import { ContentLoader } from "../../../../../../boxes/ContentLoader/ContentLoader";
import { LoadMoreObserver } from "../../../../../../shared/LoadMoreObserver/LoadMoreObserver";
import { SUBSCRIPTION_TYPES } from "../../../../../../consts/api";
import {
  capitalize,
  formatCurrency,
} from "../../../../../../utilities/general";
import classes from "../sass/Table.module.scss";
import { SALES_DECLINE_REPORTS_ORDER_BY } from "../../../../../../api/queries/useSalesDeclineReportsQuery";
import { useCurrentUserQuery } from "../../../../../../api/queries/useUserQuery";
import { PREVIEW_DATE_FORMAT } from "../../../../../../consts/general";

export function Table({ reports, order }) {
  const { tCommon } = useAppTranslation.Common();
  const { data: user } = useCurrentUserQuery();

  const isEmpty = reports.value.length === 0 && !reports.isLoading;
  const isVisible = !reports.isLoading && !isEmpty;

  const getFee = (r) => {
    if (r.monthlyFees || r.yearlyFees) {
      return formatCurrency(
        r.paymentFrequency === SUBSCRIPTION_TYPES.monthly.toLowerCase()
          ? r.monthlyFees
          : r.yearlyFees,
        r.currency,
      );
    }
    return tCommon("symbol.longDash");
  };

  const data = reports.value.map((r) => ({
    key: r.id,

    clinicName: r.clinicName || tCommon("symbol.longDash"),

    patientName: r.patientName || tCommon("symbol.longDash"),

    membershipName: r.tier || tCommon("symbol.longDash"),

    paymentDate: r.paymentDate
      ? moment(r.paymentDate).format(
          user?.previewDateFormat || PREVIEW_DATE_FORMAT,
        )
      : tCommon("symbol.longDash"),

    reason: r.reason || tCommon("symbol.longDash"),

    paymentFrequency:
      capitalize(r.paymentFrequency) || tCommon("symbol.longDash"),

    fee: getFee(r),
  }));

  const renderSortWrap = (children, field) => (
    <SortAdornment
      value={order.value.by === field ? order.value.direction : undefined}
      onToggle={() => order.update(field)}
    >
      {children}
    </SortAdornment>
  );

  return (
    <Box>
      {reports.isLoading && !reports.isSoftLoading && (
        <Skeleton count={15} height={40.5} borderRadius="0px" />
      )}
      {isEmpty && <Empty position="center" />}
      {isVisible && (
        <ContentLoader isLoading={reports.isSoftLoading}>
          <SharedTable
            bgColor="white"
            data={data}
            cols={[
              {
                data: renderSortWrap(
                  tCommon("label.clinic"),
                  SALES_DECLINE_REPORTS_ORDER_BY.clinic,
                ),
                accessor: "clinicName",
              },
              {
                data: renderSortWrap(
                  tCommon("label.patientName"),
                  SALES_DECLINE_REPORTS_ORDER_BY.patient,
                ),
                accessor: "patientName",
              },
              {
                data: renderSortWrap(
                  tCommon("label.membershipName"),
                  SALES_DECLINE_REPORTS_ORDER_BY.tier,
                ),
                accessor: "membershipName",
                headColClassName: classes.tableColMaxWidth,
              },
              {
                data: renderSortWrap(
                  tCommon("label.membershipType"),
                  SALES_DECLINE_REPORTS_ORDER_BY.paymentFrequency,
                ),
                accessor: "paymentFrequency",
              },
              {
                data: tCommon("label.amount"),
                accessor: "fee",
              },
              {
                data: renderSortWrap(
                  tCommon("label.paymentDate"),
                  SALES_DECLINE_REPORTS_ORDER_BY.paymentDate,
                ),
                accessor: "paymentDate",
              },
              {
                data: renderSortWrap(
                  tCommon("label.reason"),
                  SALES_DECLINE_REPORTS_ORDER_BY.reason,
                ),
                accessor: "reason",
                headColClassName: classes.tableColMaxWidth,
              },
            ]}
          />
          {reports.hasMore && (
            <LoadMoreObserver
              noPaddingBottom
              loadMore={reports.fetchMore}
              isLoading={reports.isNextFetching}
            />
          )}
        </ContentLoader>
      )}
    </Box>
  );
}

Table.propTypes = {
  reports: PropTypes.object.isRequired,
  order: PropTypes.object.isRequired,
};
